/**
 * What the New application card reads: create_application's approval card and its receipt
 * (`borrowers/tools/new_application.py`: `approval_card`, `result_card`), and the card's fields
 * as the Application form labels and groups them. Values come as the form saves them.
 */

export const NEW_APPLICATION = "new_application";

/** Where a value on the card came from: what the user said, the dropped PDF, or typed on it. */
export type FieldSource = "chat" | "pdf" | "edited";

export interface Funder {
  id: number;
  name: string;
}

/** The approval choice's `card`, while the call waits. */
export interface NewApplicationCardPayload {
  kind: typeof NEW_APPLICATION;
  version: number;
  values: Record<string, string>;
  sources: Record<string, FieldSource>;
  /** The user's organizations: Send to lists them when there is more than one. */
  organizations: Funder[];
  organization_ids: number[];
  /** Each field's label as a refusal of it starts ("EIN: ..."), by name. */
  labels?: Record<string, string>;
  /** The arguments the card may change (`Tool.override_fields`). */
  overrides?: string[];
  /** On the picked fake model (`Tool._card_with_overrides`): its files are held, never read or sent. */
  fake_model?: boolean;
}

export interface CreatedApplication {
  id: number;
  company_name: string;
  funder: string;
  url: string;
}

/** The tool_result's `card`, once the call ran. */
export interface NewApplicationReceipt {
  kind: typeof NEW_APPLICATION;
  version: number;
  applications: CreatedApplication[];
  /** The company and the key fields given (`RECEIPT_ROWS`). */
  values: Record<string, string>;
  /** The note's fields that were given, by name: their values stay off the receipt. */
  noted?: string[];
  intake_status: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export function isNewApplicationCard(card: unknown): card is NewApplicationCardPayload {
  return (
    isRecord(card) &&
    card.kind === NEW_APPLICATION &&
    card.version === 1 &&
    isRecord(card.values) &&
    isRecord(card.sources) &&
    Array.isArray(card.organizations) &&
    Array.isArray(card.organization_ids)
  );
}

export function isNewApplicationReceipt(card: unknown): card is NewApplicationReceipt {
  return (
    isRecord(card) &&
    card.kind === NEW_APPLICATION &&
    card.version === 1 &&
    Array.isArray(card.applications) &&
    isRecord(card.values)
  );
}

export interface CardField {
  name: string;
  label: string;
  placeholder: string;
  /** Takes the row's whole width. */
  wide?: boolean;
  required?: boolean;
  /** Picked from the entity types. */
  select?: boolean;
}

/** Details as the gallery lays it out (W1): the company, the key fields, the business address. */
export const KEY_FIELDS: CardField[] = [
  {
    name: "company_name",
    label: "Company Name",
    placeholder: "Legal business name",
    wide: true,
    required: true,
  },
  { name: "owner_name", label: "Owner Name", placeholder: "Full name" },
  { name: "ein", label: "EIN", placeholder: "12-3456789" },
  { name: "entity_type", label: "Entity Type", placeholder: "Select", select: true },
  { name: "email", label: "Email", placeholder: "owner@business.com" },
  { name: "phone", label: "Phone", placeholder: "(555) 555-0100" },
];

/** An address as one field of four inputs: street, then city, state and ZIP. */
export interface AddressGroup {
  label: string;
  fields: [CardField, CardField, CardField, CardField];
}

export const BUSINESS_ADDRESS: AddressGroup = {
  label: "Business Address",
  fields: [
    { name: "business_address_line1", label: "Street address", placeholder: "Street address" },
    { name: "business_city", label: "City", placeholder: "City" },
    { name: "business_state", label: "State", placeholder: "State" },
    { name: "business_postal_code", label: "ZIP", placeholder: "ZIP" },
  ],
};

export const HOME_ADDRESS: AddressGroup = {
  label: "Home Address",
  fields: [
    { name: "address_line1", label: "Street address", placeholder: "Street address" },
    { name: "city", label: "City", placeholder: "City" },
    { name: "state", label: "State", placeholder: "State" },
    { name: "postal_code", label: "ZIP", placeholder: "ZIP" },
  ],
};

export const DBA: CardField = {
  name: "merchants_dba",
  label: "DBA (Doing Business As)",
  placeholder: "Trade name",
  wide: true,
};

export const DRIVERS_LICENSE: CardField = {
  name: "owner_drivers_license",
  label: "Driver's License",
  placeholder: "License number",
};

/** What the form has no input for: saved on the application as a note. */
export const NOTE_FIELDS: CardField[] = [
  { name: "requested_amount", label: "Amount requested", placeholder: "$50,000" },
  { name: "industry", label: "Industry", placeholder: "Bakery" },
  { name: "business_start_date", label: "Business start date", placeholder: "Mar 2019" },
  { name: "website", label: "Website", placeholder: "business.com" },
];

/** The fields under "More fields", for its summary of what is filled. */
export const MORE_FIELDS: CardField[] = [
  DBA,
  DRIVERS_LICENSE,
  ...HOME_ADDRESS.fields,
  ...NOTE_FIELDS,
];

export const MORE_SUMMARY =
  "DBA, driver's license, owner's home address, amount requested, industry, start date, website";

/** Every field the card edits. */
export const CARD_FIELDS: string[] = [
  ...KEY_FIELDS,
  ...BUSINESS_ADDRESS.fields,
  ...MORE_FIELDS,
].map((field) => field.name);

/** The receipt's summary, two long values per row taking two columns (W9). */
export const RECEIPT_ROWS: { name: string; label: string; wide?: boolean }[] = [
  { name: "company_name", label: "Company", wide: true },
  { name: "owner_name", label: "Owner" },
  { name: "email", label: "Email", wide: true },
  { name: "entity_type", label: "Entity" },
  { name: "ein", label: "EIN" },
  { name: "phone", label: "Phone" },
];

/** The address group a field is one input of, if any: the card chips and counts it once. */
export function addressOf(name: string): AddressGroup | null {
  return (
    [BUSINESS_ADDRESS, HOME_ADDRESS].find((group) =>
      group.fields.some((field) => field.name === name)
    ) ?? null
  );
}

/** A refusal of one field: its name and what the server says of it. */
export interface FieldRefusal {
  name: string;
  message: string;
}

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The fields a server refusal names and what it says of each: `error` as the server joins them
 * ("EIN: Enter a valid EIN. Email: Enter a valid email address."), each starting with the label
 * the card's `labels` give the field (`new_application.CARD_LABELS`). Empty for a refusal of no
 * one field.
 */
export function refusedFields(
  error: string | null,
  labels: Record<string, string> | undefined
): FieldRefusal[] {
  const names = new Map(Object.entries(labels ?? {}).map(([name, label]) => [label, name]));
  if (!error || !names.size) return [];
  // The longest label first, so "Business Address" is not read as "Address".
  const alternatives = [...names.keys()].sort((a, b) => b.length - a.length).map(escape);
  const starts = [...error.matchAll(new RegExp(`(?:^|\\s)(${alternatives.join("|")}): `, "g"))];
  return starts.map((match, index) => {
    const label = match[1];
    const from = match.index + match[0].length;
    const to = index + 1 < starts.length ? starts[index + 1].index : error.length;
    return { name: names.get(label)!, message: error.slice(from, to).trim() };
  });
}
